/**
 * Acumula las muestras de la sonda en ventanas de 5 minutos.
 *
 * Toda la matematica de la sonda vive aca, sin timers ni sockets, para
 * poder probarla sin navegador. `NetworkProbe` solo aporta el reloj y el
 * transporte.
 */

import {
    averageOf, jitterMaxOf, jitterOf, score,
} from './score';
import { UdpProbeResult } from './udp';

export const BUCKET_MS = 5 * 60 * 1000;

export interface BucketPayload {
    bucket_at: string;
    samples: number;
    timeouts: number;
    reconnects: number;
    rtt_min: number | null;
    rtt_avg: number | null;
    rtt_max: number | null;
    jitter_avg: number | null;
    jitter_max: number | null;
    server_load_avg: number | null;
    udp_probe_ok: boolean;
    udp_rtt_avg: number | null;
    udp_jitter_avg: number | null;
    udp_loss_pct: number | null;
    ice_candidate_type: string | null;
    effective_type: string | null;
    downlink: number | null;
}

export interface ConnectionHint {
    effectiveType: string | null;
    downlink: number | null;
}

interface Window {
    start: number;
    rtts: number[];
    serverLoads: number[];
    timeouts: number;
    reconnects: number;
    udp: UdpProbeResult | null;
}

function emptyWindow(start: number): Window {
    return {
        start,
        rtts: [],
        serverLoads: [],
        timeouts: 0,
        reconnects: 0,
        udp: null,
    };
}

interface NetworkInformation {
    effectiveType?: string;
    downlink?: number;
}

interface NavigatorWithConnection {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
}

/** Lee `navigator.connection`, que existe solo en Chromium. */
export function connectionHint(): ConnectionHint {
    if (typeof navigator === 'undefined') {
        return { effectiveType: null, downlink: null };
    }

    const nav = navigator as unknown as NavigatorWithConnection;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

    if (!connection) {
        return { effectiveType: null, downlink: null };
    }

    return {
        effectiveType: connection.effectiveType || null,
        downlink: typeof connection.downlink === 'number' ? connection.downlink : null,
    };
}

export function windowStartFor(at: number): number {
    return Math.floor(at / BUCKET_MS) * BUCKET_MS;
}

export default class BucketAccumulator {
    private current: Window | null = null;

    /** Ventana en curso, para el indicador en vivo. */
    get live() {
        const window = this.current;

        if (!window) {
            return {
                samples: 0,
                timeouts: 0,
                reconnects: 0,
                rtt: null as number | null,
                jitter: null as number | null,
                serverLoad: null as number | null,
                udp: null as UdpProbeResult | null,
                score: null as number | null,
            };
        }

        const rtt = averageOf(window.rtts);
        const jitter = jitterOf(window.rtts);

        return {
            samples: window.rtts.length,
            timeouts: window.timeouts,
            reconnects: window.reconnects,
            rtt,
            jitter,
            serverLoad: averageOf(window.serverLoads),
            udp: window.udp,
            score: score({
                samples: window.rtts.length,
                timeouts: window.timeouts,
                rttAvg: rtt,
                jitterAvg: jitter,
                udpProbeOk: window.udp?.ok === true,
                udpRttAvg: window.udp?.rttAvg,
                udpJitterAvg: window.udp?.jitterAvg,
                udpLossPct: window.udp?.lossPct,
            }),
        };
    }

    /**
     * Registra una muestra. Devuelve la ventana anterior cuando el reloj
     * cruzo el limite de 5 minutos, o `null` si todavia no cerro.
     */
    addSample(rtt: number, serverLoad: number | null, at: number): BucketPayload | null {
        const closed = this.rotate(at);
        const window = this.current as Window;

        window.rtts.push(rtt);
        if (serverLoad !== null) {
            window.serverLoads.push(serverLoad);
        }

        return closed;
    }

    addTimeout(at: number): BucketPayload | null {
        const closed = this.rotate(at);
        (this.current as Window).timeouts += 1;

        return closed;
    }

    addReconnect(at: number): BucketPayload | null {
        const closed = this.rotate(at);
        (this.current as Window).reconnects += 1;

        return closed;
    }

    setUdp(result: UdpProbeResult, at: number): BucketPayload | null {
        const closed = this.rotate(at);
        (this.current as Window).udp = result;

        return closed;
    }

    /** Cierra la ventana en curso, para cuando el usuario se va del panel. */
    flush(): BucketPayload | null {
        if (!this.current || !this.hasData(this.current)) {
            this.current = null;
            return null;
        }

        const payload = this.payloadOf(this.current);
        this.current = null;

        return payload;
    }

    private rotate(at: number): BucketPayload | null {
        const start = windowStartFor(at);

        if (!this.current) {
            this.current = emptyWindow(start);
            return null;
        }

        if (this.current.start === start) {
            return null;
        }

        const previous = this.current;
        this.current = emptyWindow(start);

        return this.hasData(previous) ? this.payloadOf(previous) : null;
    }

    // eslint-disable-next-line class-methods-use-this
    private hasData(window: Window): boolean {
        return window.rtts.length > 0 || window.timeouts > 0 || window.reconnects > 0;
    }

    // eslint-disable-next-line class-methods-use-this
    private payloadOf(window: Window): BucketPayload {
        const hint = connectionHint();

        // el contrato con el canal `network` es snake_case, igual que el
        // resto de la api del panel. mismo criterio que i18n/es.ts.
        /* eslint-disable @typescript-eslint/camelcase */
        return {
            bucket_at: new Date(window.start).toISOString(),
            samples: window.rtts.length,
            timeouts: window.timeouts,
            reconnects: window.reconnects,
            rtt_min: window.rtts.length ? Math.min(...window.rtts) : null,
            rtt_avg: averageOf(window.rtts),
            rtt_max: window.rtts.length ? Math.max(...window.rtts) : null,
            jitter_avg: jitterOf(window.rtts),
            jitter_max: jitterMaxOf(window.rtts),
            server_load_avg: averageOf(window.serverLoads),
            udp_probe_ok: window.udp?.ok === true,
            udp_rtt_avg: window.udp?.rttAvg ?? null,
            udp_jitter_avg: window.udp?.jitterAvg ?? null,
            udp_loss_pct: window.udp?.lossPct ?? null,
            ice_candidate_type: window.udp?.candidateType ?? null,
            effective_type: hint.effectiveType,
            downlink: hint.downlink,
        };
        /* eslint-enable @typescript-eslint/camelcase */
    }
}
